import { useCallback, type SyntheticEvent } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import type { AccountSidebarProps } from "./types";
import { useProfilePictureSrc } from "@/utils/profilePicture";
import {
  DEFAULT_ACTION_COUNTS,
  WEBSITE_ACTION_TYPES,
} from "@/types/activity.types";
import { Trash2, Upload } from "lucide-react";

/**
 * Displays the avatar, membership badge, and statistics summary for the signed-in user.
 */
export function AccountSidebar({
  currentUser,
  statistics,
  profilePicture,
  profilePicturePreview,
  language,
  onProfilePictureError,
  onProfilePictureChange,
  onProfilePictureRemove,
  isEditingPicture,
  showProfilePictureHint,
  onProfilePictureHintClick,
  t,
}: AccountSidebarProps) {
  const avatarFallbackStyle = {
    backgroundColor: "color-mix(in srgb, var(--accent) 18%, var(--background))",
    color: "color-mix(in srgb, var(--foreground) 82%, var(--surface))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 82%, transparent)",
  } as const;
  const memberSinceLocale = language === "de" ? "de-DE" : "en-US";
  const resolvedStats = statistics ?? {
    memberSince: "",
    activityCount: 0,
    actionCounts: { ...DEFAULT_ACTION_COUNTS },
  };
  const memberSinceLabel = resolvedStats.memberSince
    ? new Date(resolvedStats.memberSince).toLocaleDateString(
        memberSinceLocale,
        {
          year: "numeric",
          month: "long",
          day: "numeric",
        }
      )
    : t("common.loading");

  const {
    src: profilePictureSrc,
    onError: profilePictureErrorHandler,
  } = useProfilePictureSrc(profilePicture);
  const displaySrc = profilePicturePreview || profilePictureSrc;
  const uploadLabel = displaySrc
    ? t("profilePicture.change")
    : t("profilePicture.select");
  const canRemove = Boolean(profilePictureSrc || isEditingPicture);

  const handleProfilePictureError = useCallback(
    (event: SyntheticEvent<HTMLImageElement, Event>) => {
      profilePictureErrorHandler(event);
      onProfilePictureError?.();
    },
    [onProfilePictureError, profilePictureErrorHandler]
  );

  return (
    <div className="space-y-6">
      <Card className="stat-card">
        <CardHeader>
          <div className="flex flex-col items-center gap-4">
            <div className="relative group">
              <button
                type="button"
                onClick={onProfilePictureChange}
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label={uploadLabel}
                title={uploadLabel}
              >
                <Avatar className="w-24 h-24">
                  <AvatarImage
                    src={displaySrc}
                    onError={profilePicturePreview ? undefined : handleProfilePictureError}
                    // Helps in some setups when proxies/CDNs behave oddly
                    referrerPolicy="no-referrer"
                  />
                  <AvatarFallback className="text-2xl" style={avatarFallbackStyle}>
                    {currentUser.username.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </button>

              <div className="absolute inset-x-0 bottom-5 z-10 flex justify-center gap-2 opacity-100 transition md:pointer-events-none md:opacity-0 md:group-hover:pointer-events-auto md:group-hover:opacity-100 md:group-focus-within:pointer-events-auto md:group-focus-within:opacity-100">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full border-border bg-background/90 shadow-sm backdrop-blur"
                  onClick={onProfilePictureChange}
                  aria-label={uploadLabel}
                  title={uploadLabel}
                >
                  <Upload className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full border-border bg-background/90 shadow-sm backdrop-blur"
                  onClick={onProfilePictureRemove}
                  disabled={!canRemove}
                  aria-label={t("profilePicture.remove")}
                  title={t("profilePicture.remove")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="text-center mt-6 min-w-0">
              <CardTitle className="text-primary-heading break-words [overflow-wrap:anywhere] whitespace-normal">
                {currentUser.username}
              </CardTitle>
              <CardDescription
                className="card-description-text break-all whitespace-normal"
                style={primaryTextStyle}
              >
                {currentUser.email}
              </CardDescription>
              <Badge variant={currentUser.role === "admin" ? "default" : "secondary"}>
                {currentUser.role === "admin"
                  ? t("account.administrator")
                  : t("account.registeredUser")}
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-secondary mb-1">
                {t("account.memberSince")}
              </p>
              <p className="text-primary-heading">
                {memberSinceLabel}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {showProfilePictureHint && (
        <button
          type="button"
          onClick={onProfilePictureHintClick}
          className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm transition"
          style={{
            borderColor: "var(--primary)",
            color: "var(--primary)",
          }}
        >
          {t("profilePicture.mobileHint")}
        </button>
      )}

      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="text-primary-heading">
            {t("account.statistics")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-secondary">
              {t("account.totalActivity")}
            </span>
            <span className="text-primary-heading">
              {resolvedStats.activityCount}
            </span>
          </div>
          {WEBSITE_ACTION_TYPES.map((action) => (
            <div key={action} className="flex justify-between items-center">
              <span className="text-secondary">{t(`activity.${action}`)}</span>
              <span className="text-primary-heading">
                {resolvedStats.actionCounts[action] ?? 0}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
